import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, Upload, Download } from "lucide-react";

const emptyVoce = { code: "", description: "", unit: "cad", price: 0 };

export default function ListiniPage() {
  const { t } = useTranslation();
  const [committenti, setCommittenti] = useState([]);
  const [listini, setListini] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ committente_id: "", name: "", voci: [] });

  const [openImport, setOpenImport] = useState(false);
  const [impCommittente, setImpCommittente] = useState("");
  const [impName, setImpName] = useState("");
  const [impFile, setImpFile] = useState(null);

  const load = async () => setListini((await api.get("/listini")).data);
  useEffect(() => {
    api.get("/committenti").then((r) => setCommittenti(r.data));
    load();
  }, []);

  const openNew = () => { setEditingId(null); setForm({ committente_id: "", name: "", voci: [{ ...emptyVoce }] }); setOpen(true); };
  const openEdit = (l) => { setEditingId(l.id); setForm({ committente_id: l.committente_id, name: l.name, voci: l.voci || [] }); setOpen(true); };

  const addVoce = () => setForm({ ...form, voci: [...form.voci, { ...emptyVoce }] });
  const updVoce = (i, k, v) => {
    const voci = [...form.voci];
    voci[i] = { ...voci[i], [k]: k === "price" ? Number(v) || 0 : v };
    setForm({ ...form, voci });
  };
  const rmVoce = (i) => setForm({ ...form, voci: form.voci.filter((_, idx) => idx !== i) });

  const save = async () => {
    if (!form.committente_id || !form.name) return toast.error(t("auto.committente_e_nome_richiesti"));
    try {
      if (editingId) await api.patch(`/listini/${editingId}`, form);
      else await api.post("/listini", form);
      toast.success(t("common.saved"));
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare listino?")) return;
    try { await api.delete(`/listini/${id}`); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doImport = async () => {
    if (!impCommittente || !impName || !impFile) return toast.error(t("auto.committente_nome_e_file_richiesti"));
    const form = new FormData();
    form.append("file", impFile);
    try {
      await api.post(`/listini/upload-excel?committente_id=${impCommittente}&name=${encodeURIComponent(impName)}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Listino importato");
      setOpenImport(false);
      setImpFile(null); setImpName(""); setImpCommittente("");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/listini/template.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "template_listino.xlsx";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Template scaricato");
    } catch (e) { toast.error("Errore download"); }
  };

  const commName = (id) => committenti.find((c) => c.id === id)?.name || "—";

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Configurazione</div>
          <h1 className="mt-1 text-3xl font-bold text-slate-900 lg:text-4xl">{t("menu.price_lists")}</h1>
        </div>
        <Button onClick={openNew} className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="btn-new-listino"><Plus className="mr-2 h-4 w-4" /> Nuovo listino</Button>
        <Button variant="outline" onClick={() => setOpenImport(true)} className="rounded-sm" data-testid="btn-import-listino"><Upload className="mr-2 h-4 w-4" /> {t("auto.import_excel")}</Button>
        <Button variant="outline" onClick={downloadTemplate} className="rounded-sm" data-testid="btn-template-listino"><Download className="mr-2 h-4 w-4" /> Scarica template Excel</Button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {listini.map((l) => (
          <div key={l.id} className="rounded-sm border border-slate-200 bg-white p-5">
            <div className="mb-3 flex items-start justify-between">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{commName(l.committente_id)}</div>
                <div className="mt-1 text-lg font-semibold text-slate-900">{l.name}</div>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => openEdit(l)}><Pencil className="h-4 w-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => del(l.id)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
              </div>
            </div>
            <table className="w-full text-xs">
              <thead className="border-b border-slate-200 text-left font-bold uppercase tracking-widest text-slate-500">
                <tr><th className="py-1">{t("common.code")}</th><th>{t("common.description")}</th><th className="text-right">{t("common.price")}</th></tr>
              </thead>
              <tbody>
                {(l.voci || []).map((v, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="py-1 font-mono">{v.code}</td>
                    <td>{v.description}</td>
                    <td className="text-right font-mono">€ {Number(v.price).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
        {listini.length === 0 && <div className="col-span-2 rounded-sm border border-dashed border-slate-300 p-10 text-center text-slate-400">{t("auto.nessun_listino_creane_uno_per_iniziare")}</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>{editingId ? "Modifica listino" : "Nuovo listino"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t("auto.committente_x")}</Label>
              <Select value={form.committente_id} onValueChange={(v) => setForm({ ...form, committente_id: v })}>
                <SelectTrigger className="rounded-sm" data-testid="listino-committente"><SelectValue placeholder="Scegli" /></SelectTrigger>
                <SelectContent>{committenti.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Nome listino</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" data-testid="listino-name" /></div>
          </div>

          <div className="mt-3">
            <div className="mb-2 flex items-center justify-between">
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Voci</Label>
              <Button size="sm" variant="outline" onClick={addVoce} className="rounded-sm" data-testid="listino-add-voce"><Plus className="mr-1 h-3 w-3" /> Aggiungi voce</Button>
            </div>
            <div className="max-h-80 space-y-2 overflow-y-auto">
              {form.voci.map((v, i) => (
                <div key={i} className="grid grid-cols-12 gap-2">
                  <Input placeholder="Codice" value={v.code} onChange={(e) => updVoce(i, "code", e.target.value)} className="col-span-2 rounded-sm font-mono" />
                  <Input placeholder="Descrizione" value={v.description} onChange={(e) => updVoce(i, "description", e.target.value)} className="col-span-6 rounded-sm" />
                  <Input placeholder="U.M." value={v.unit} onChange={(e) => updVoce(i, "unit", e.target.value)} className="col-span-1 rounded-sm" />
                  <Input type="number" step="0.01" placeholder={t("auto.prezzo_x")} value={v.price} onChange={(e) => updVoce(i, "price", e.target.value)} className="col-span-2 rounded-sm font-mono" />
                  <Button size="sm" variant="ghost" onClick={() => rmVoce(i)} className="col-span-1"><Trash2 className="h-4 w-4 text-red-600" /></Button>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter><Button onClick={save} className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="listino-save">{t("common.save")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={openImport} onOpenChange={setOpenImport}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Importa listino da Excel/CSV</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t("auto.committente_x")}</Label>
              <Select value={impCommittente} onValueChange={setImpCommittente}>
                <SelectTrigger className="rounded-sm" data-testid="import-committente"><SelectValue placeholder="Scegli" /></SelectTrigger>
                <SelectContent>{committenti.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Nome listino</Label>
              <Input value={impName} onChange={(e) => setImpName(e.target.value)} placeholder="es. Listino FiberCop 2026" className="rounded-sm" data-testid="import-name" />
            </div>
            <div>
              <Label>File Excel/CSV</Label>
              <Input type="file" accept=".xlsx,.xls,.csv" onChange={(e) => setImpFile(e.target.files?.[0])} className="rounded-sm" data-testid="import-file" />
              <p className="mt-1 text-xs text-slate-500">Colonne richieste: <code className="font-mono">code</code> (o codice), <code className="font-mono">description</code> (o descrizione), <code className="font-mono">price</code> (o prezzo). Opzionale: <code className="font-mono">unit</code> (o u.m.).</p>
              <button type="button" onClick={downloadTemplate} className="mt-2 text-xs font-medium text-[#0052FF] hover:underline">
                ↓ Scarica template pronto con esempi
              </button>
            </div>
          </div>
          <DialogFooter><Button onClick={doImport} className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="import-listino-confirm">{t("auto.importa")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
